import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SavedViewSelector } from "./SavedViewSelector";
import { useSavedHeaderViews } from "./useSavedHeaderViews";
import { withFixedLeadingKeys } from "./fixedLeadingColumns";

// Single entry point for the "My Attendance"-style saved-view control: it
// bundles useSavedHeaderViews + SavedViewSelector (the "Default View ▾"
// dropdown, as used on My Zone > My Attendance) into the two values a screen
// hands to CustomFilter/its table — `visibleHeaders` (the active view's
// ordered columns) and `titleOverride` (the dropdown itself). It mirrors the
// wiring that lived inline in pages/MyZone/WorkingHours/index.js so every My
// Requests screen can adopt the same look without re-implementing ~200 lines
// of view plumbing.
//
//   const { visibleHeaders, titleOverride } = useSavedViewSelector({
//     domain, userId: userDetails?.id, subModuleId,
//     storageKey: "<module>_saved_views",
//     fullHeaders: MyReportHeaders,          // typed {name,key,type,icon}
//     defaultHeaderKeys: ["date", "status"], // optional; defaults to all keys
//     screenAccess: check_access,
//     onViewChange: () => setPage(0),
//   });
//
//   <CustomFilter filterHeaders={visibleHeaders} titleOverride={titleOverride}
//     labeledToolbar ... />
//   <MyReportTable headers={visibleHeaders} ... />
export const useSavedViewSelector = ({
  domain,
  userId,
  subModuleId,
  storageKey,
  scope = "default",
  fullHeaders = [],
  defaultHeaderKeys,
  screenAccess = true,
  onViewChange,
  // Optional column keys that always come first, in this order, in every
  // view, and can't be dragged in the view editor.
  fixedLeadingKeys = null,
}) => {
  // A stable per-storageKey id for the built-in default view, so each screen's
  // "Default View" is namespaced and never collides with another screen's.
  const defaultViewId = useMemo(
    () => `__default_view__${storageKey}`,
    [storageKey],
  );

  const [currentViewId, setCurrentViewId] = useState(null);

  const handleViewChange = useCallback(() => {
    onViewChange?.();
  }, [onViewChange]);

  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId,
    subModuleId,
    storageKey,
    scope,
    onViewChange: handleViewChange,
    defaultViewId,
  });

  const persistedDefaultViewId = activeViewId || defaultViewId;

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== defaultViewId) return prev;
      return persistedDefaultViewId;
    });
  }, [defaultViewId, persistedDefaultViewId]);

  // The built-in default columns: the caller's chosen subset, or all headers.
  const baseDefaultHeaders = useMemo(() => {
    if (!defaultHeaderKeys?.length) return fullHeaders;
    const keys = new Set(defaultHeaderKeys);
    return fullHeaders.filter((header) => keys.has(header.key));
  }, [defaultHeaderKeys, fullHeaders]);

  const storedDefaultView = useMemo(
    () => savedViewsForScope.find((view) => view.id === defaultViewId) || null,
    [savedViewsForScope, defaultViewId],
  );

  const customSavedViews = useMemo(
    () => savedViewsForScope.filter((view) => view.id !== defaultViewId),
    [savedViewsForScope, defaultViewId],
  );

  const defaultView = useMemo(() => {
    if (storedDefaultView?.isCustomDefault) {
      // Whatever headerConfig the user saved is authoritative — same as any
      // other saved view. (Previously this back-filled any header missing
      // from headerConfig, meant to surface columns added to the schema
      // after the view was saved — but that's indistinguishable from a
      // column the user deliberately unchecked, so it silently re-added
      // every unchecked header right back on the next render.)
      return storedDefaultView;
    }

    return {
      id: defaultViewId,
      name: "Default View",
      headerConfig: baseDefaultHeaders.map((header) => ({
        key: header.key,
        name: header.name,
      })),
      headerKeys: baseDefaultHeaders.map((header) => header.key),
      isDefaultView: true,
    };
  }, [baseDefaultHeaders, defaultViewId, storedDefaultView]);

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === defaultViewId) return defaultView;
    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, defaultViewId, effectiveViewId]);

  const viewHeaders = useMemo(() => {
    const headerByKey = new Map(
      fullHeaders.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return { ...baseHeader, name: entry.name?.trim() || baseHeader.name };
        })
        .filter(Boolean);
      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filtered = fullHeaders.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filtered.length ? filtered : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders, fullHeaders]);

  const visibleHeaders = useMemo(
    () => withFixedLeadingKeys(viewHeaders, fixedLeadingKeys),
    [viewHeaders, fixedLeadingKeys],
  );

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleViewChange();
    },
    [handleViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(defaultViewId);
    handleViewChange();
  }, [defaultViewId, handleViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === defaultViewId) {
        selectDefaultView();
        setCurrentViewId(defaultViewId);
        return;
      }
      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [defaultViewId, selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) setCurrentViewId(defaultViewId);
      deleteView(viewId);
    },
    [currentViewId, defaultViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || defaultViewId,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(defaultViewId);
    },
    [defaultViewId, upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={fullHeaders}
        fixedLeadingKeys={fixedLeadingKeys}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={screenAccess}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === defaultViewId) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      customSavedViews,
      defaultView,
      defaultViewId,
      effectiveViewId,
      fullHeaders,
      fixedLeadingKeys,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      persistedDefaultViewId,
      screenAccess,
      updateView,
    ],
  );

  return { visibleHeaders, titleOverride, activeView };
};
